(() => {
  'use strict';

  const focusable = 'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';

  function trapFocus(container, event) {
    if (event.key !== 'Tab') return;
    const items = [...container.querySelectorAll(focusable)].filter((el) => el.offsetParent !== null);
    if (!items.length) return;
    const first = items[0];
    const last = items[items.length - 1];
    if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
    if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
  }

  function initDrawers(root = document) {
    root.querySelectorAll('[data-drawer]').forEach((drawer) => {
      if (drawer.dataset.ready) return;
      drawer.dataset.ready = 'true';
      const id = drawer.id;
      let opener = null;
      const close = () => {
        drawer.classList.remove('is-open');
        drawer.setAttribute('aria-hidden', 'true');
        document.body.classList.remove('is-locked');
        document.querySelectorAll(`[aria-controls="${id}"]`).forEach((button) => button.setAttribute('aria-expanded', 'false'));
        if (opener) opener.focus();
      };
      const open = (button) => {
        opener = button;
        drawer.classList.add('is-open');
        drawer.setAttribute('aria-hidden', 'false');
        document.body.classList.add('is-locked');
        document.querySelectorAll(`[aria-controls="${id}"]`).forEach((trigger) => trigger.setAttribute('aria-expanded', 'true'));
        setTimeout(() => drawer.querySelector(focusable)?.focus(), 30);
      };
      document.querySelectorAll(`[data-drawer-open="${id}"]`).forEach((button) => button.addEventListener('click', () => open(button)));
      drawer.querySelectorAll('[data-drawer-close]').forEach((button) => button.addEventListener('click', close));
      drawer.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') close();
        trapFocus(drawer, event);
      });
    });
  }

  function initSearch(root = document) {
    root.querySelectorAll('[data-search-modal]').forEach((modal) => {
      if (modal.dataset.ready) return;
      modal.dataset.ready = 'true';
      let opener = null;
      const close = () => {
        modal.classList.remove('is-open');
        modal.setAttribute('aria-hidden', 'true');
        document.body.classList.remove('is-locked');
        opener?.focus();
      };
      document.querySelectorAll('[data-search-open]').forEach((button) => button.addEventListener('click', () => {
        opener = button;
        modal.classList.add('is-open');
        modal.setAttribute('aria-hidden', 'false');
        document.body.classList.add('is-locked');
        setTimeout(() => modal.querySelector('input[type="search"]')?.focus(), 30);
      }));
      modal.querySelectorAll('[data-search-close]').forEach((button) => button.addEventListener('click', close));
      modal.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') close();
        trapFocus(modal, event);
      });
    });
  }

  function initGalleries(root = document) {
    root.querySelectorAll('[data-gallery]').forEach((gallery) => {
      if (gallery.dataset.ready) return;
      gallery.dataset.ready = 'true';
      const slides = [...gallery.querySelectorAll('[data-gallery-slide]')];
      const thumbs = [...gallery.querySelectorAll('[data-gallery-thumb]')];
      thumbs.forEach((thumb, index) => thumb.addEventListener('click', () => {
        slides.forEach((slide, i) => slide.classList.toggle('is-active', i === index));
        thumbs.forEach((item, i) => {
          item.classList.toggle('is-active', i === index);
          item.setAttribute('aria-current', i === index ? 'true' : 'false');
        });
      }));
    });
  }

  function initTabs(root = document) {
    root.querySelectorAll('[data-tabs]').forEach((tabs) => {
      if (tabs.dataset.ready) return;
      tabs.dataset.ready = 'true';
      const triggers = [...tabs.querySelectorAll('[role="tab"]')];
      const panels = [...tabs.querySelectorAll('[role="tabpanel"]')];
      const activate = (index, focus = false) => {
        triggers.forEach((trigger, i) => {
          const active = i === index;
          trigger.classList.toggle('is-active', active);
          trigger.setAttribute('aria-selected', active ? 'true' : 'false');
          trigger.tabIndex = active ? 0 : -1;
          if (active && focus) trigger.focus();
        });
        panels.forEach((panel, i) => {
          panel.classList.toggle('is-active', i === index);
          panel.hidden = i !== index;
        });
      };
      triggers.forEach((trigger, index) => {
        trigger.addEventListener('click', () => activate(index));
        trigger.addEventListener('keydown', (event) => {
          if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
          event.preventDefault();
          let next = event.key === 'Home' ? 0 : event.key === 'End' ? triggers.length - 1 : index + (event.key === 'ArrowRight' ? 1 : -1);
          if (next < 0) next = triggers.length - 1;
          if (next >= triggers.length) next = 0;
          activate(next, true);
        });
      });
      activate(Math.max(0, triggers.findIndex((item) => item.classList.contains('is-active'))));
    });
  }

  function initSort(root = document) {
    root.querySelectorAll('[data-sort-select]').forEach((select) => {
      if (select.dataset.ready) return;
      select.dataset.ready = 'true';
      select.addEventListener('change', () => {
        const url = new URL(window.location.href);
        url.searchParams.set('sort_by', select.value);
        window.location.assign(url.toString());
      });
    });
  }

  function initHeader(root = document) {
    root.querySelectorAll('[data-sticky-header]').forEach((header) => {
      if (header.dataset.ready) return;
      header.dataset.ready = 'true';
      const update = () => header.classList.toggle('is-scrolled', window.scrollY > 24);
      update();
      window.addEventListener('scroll', update, { passive: true });
    });
  }

  function init(root = document) {
    initDrawers(root);
    initSearch(root);
    initGalleries(root);
    initTabs(root);
    initSort(root);
    initHeader(root);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();

  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:block:select', (event) => {
    const details = event.target.closest('details');
    if (details) details.open = true;
  });
})();
